import { useEffect, useRef } from 'react'
import { pinSvg } from '@/components/mapPins'
import { LatLng, toLatLng } from './NearbyDriverRouteMap'
import { ROUTE_PIN_DESTINATION_COLOR, ROUTE_PIN_ORIGIN_COLOR } from '@/constants/mapPins'

/** A Numeo teardrop pin as a Google marker icon (same asset the driver route map uses). */
const pinIcon = (g: typeof google.maps, color: string, letter: string): google.maps.Icon => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(pinSvg(color, letter))}`,
    scaledSize: new g.Size(28, 40),
    anchor: new g.Point(14, 40),
})

/** One segment of a route (origin→stop, stop→stop, stop→destination) as Google formats it. */
export interface BtRouteLeg {
    distance: string
    duration: string
}

/** A single road-snapped route (the primary and each alternate), with per-leg + whole-trip totals. */
export interface BtRouteSummary {
    /** Google's short road label, e.g. "I-15 N" — empty for an unnamed route. */
    label: string
    legs: BtRouteLeg[]
    /** Whole-trip totals, summed across legs (Google only formats per-leg text). */
    distance: string
    duration: string
}

/** Meters → "1,136 mi". */
const fmtMiles = (meters: number): string => `${Math.round(meters / 1609.34).toLocaleString()} mi`

/** Seconds → "17 hr 32 min" (drops a zero hour/minute part). */
const fmtDuration = (seconds: number): string => {
    const totalMin = Math.round(seconds / 60)
    const h = Math.floor(totalMin / 60)
    const m = totalMin % 60
    if (h && m) return `${h} hr ${m} min`
    if (h) return `${h} hr`
    return `${m} min`
}

/** Collapse a Google route into per-leg text + whole-trip totals (summed from each leg's meter/second values). */
const summarizeRoute = (route: google.maps.DirectionsRoute): BtRouteSummary => {
    let meters = 0
    let seconds = 0
    const legs = route.legs.map((l) => {
        meters += l.distance?.value ?? 0
        seconds += l.duration?.value ?? 0
        return { distance: l.distance?.text ?? '—', duration: l.duration?.text ?? '—' }
    })
    return { label: route.summary ?? '', legs, distance: fmtMiles(meters), duration: fmtDuration(seconds) }
}

interface BoxTruckLoadMapEditorMapProps {
    origin: LatLng
    destination: LatLng
    /** Intermediate stops (max 3, enforced by the caller). */
    waypoints: LatLng[]
    /** Which alternate to highlight/render (0 = primary). Clamped to the available routes. */
    routeIndex?: number
    onOriginChange: (ll: LatLng) => void
    onDestinationChange: (ll: LatLng) => void
    onWaypointChange: (index: number, ll: LatLng) => void
    /**
     * Whether this map offers to CHANGE the route. False for a view of it — the inline mini map in the load
     * drawer — where the pins do not drag and the alternates are not drawn, because the drawer has no route
     * to save: a pin that moves or a grey line that highlights under the cursor promises an edit nothing
     * downstream reads. The planner modal is where a point is moved and an alternate is chosen.
     */
    editable?: boolean
    /** Lifts the freshly-computed routes (primary + alternates) so the panel can show mileage/time + a selector. */
    onRoutes?: (routes: BtRouteSummary[]) => void
    /** Fired when an alternate's grey line is clicked on the map — the caller updates `routeIndex`. */
    onSelectRoute?: (index: number) => void
}

/**
 * The large, EDITABLE Google route map: origin (A), destination (B) and up to 3 waypoints (1–3) are draggable
 * markers, and the road-snapped route is recomputed via DirectionsService whenever any point moves. Rendered
 * ONLY inside <GoogleMapsLoader> (window.google.maps ready). Mirrors NearbyDriverRouteMap's raw-primitives
 * approach (no AdvancedMarker/mapId needed) — legacy g.Marker supports `draggable` + the `dragend` event.
 */
export const BoxTruckLoadMapEditorMap = ({
    origin,
    destination,
    waypoints,
    routeIndex = 0,
    editable = true,
    onOriginChange,
    onDestinationChange,
    onWaypointChange,
    onRoutes,
    onSelectRoute,
}: BoxTruckLoadMapEditorMapProps) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const mapRef = useRef<google.maps.Map | null>(null)
    const rendererRef = useRef<google.maps.DirectionsRenderer | null>(null)
    const markersRef = useRef<google.maps.Marker[]>([])
    // Last Directions result + the grey polylines for the UNselected alternates, so map-clicks can pick a route.
    const resultRef = useRef<google.maps.DirectionsResult | null>(null)
    const altLinesRef = useRef<google.maps.Polyline[]>([])
    // Keep the latest callbacks so a marker's dragend listener never fires a stale closure.
    const cbRef = useRef({ onOriginChange, onDestinationChange, onWaypointChange, onRoutes, onSelectRoute })
    cbRef.current = { onOriginChange, onDestinationChange, onWaypointChange, onRoutes, onSelectRoute }
    // Read through a ref for the same reason the callbacks are: `drawAlternates` is called from the route
    // effects, and closing over the prop directly would put this function in their dependency lists — a new
    // identity every render, so every render would recompute the route it just drew.
    const editableRef = useRef(editable)
    editableRef.current = editable

    // Draw every route EXCEPT the selected one as a clickable grey line (the selected route is drawn blue by the
    // DirectionsRenderer). Google-Maps-style: click a grey alternate to make it the active route.
    //
    // Only where that click leads somewhere. A grey line that darkens under the cursor says "pick me", and on
    // a view of the route there is nothing to pick: `onSelectRoute` is undefined, so the click would land on
    // nothing. Not drawing them is the same answer `editable` gives the pins.
    const drawAlternates = (selectedIndex: number) => {
        const g = window.google?.maps
        const map = mapRef.current
        const result = resultRef.current
        altLinesRef.current.forEach((l) => l.setMap(null))
        altLinesRef.current = []
        if (!g || !map || !result || !editableRef.current) return
        result.routes.forEach((route, index) => {
            if (index === selectedIndex || !route.overview_path?.length) return
            const line = new g.Polyline({
                path: route.overview_path,
                map,
                strokeColor: '#9CA3AF',
                strokeWeight: 5,
                strokeOpacity: 0.6,
                zIndex: 1,
                clickable: true,
            })
            line.addListener('click', () => cbRef.current.onSelectRoute?.(index))
            line.addListener('mouseover', () => line.setOptions({ strokeColor: '#6B7280', strokeOpacity: 0.9 }))
            line.addListener('mouseout', () => line.setOptions({ strokeColor: '#9CA3AF', strokeOpacity: 0.6 }))
            altLinesRef.current.push(line)
        })
    }

    // Create the map + renderer ONCE and frame it to the initial points. preserveViewport keeps drags from
    // yanking the camera; we only fit bounds here on first mount.
    useEffect(() => {
        const g = window.google?.maps
        if (!g || !containerRef.current || mapRef.current) return
        const map = new g.Map(containerRef.current, { center: origin, zoom: 6, mapTypeControl: false, streetViewControl: false, fullscreenControl: false })
        mapRef.current = map
        rendererRef.current = new g.DirectionsRenderer({
            map,
            suppressMarkers: true,
            preserveViewport: true,
            polylineOptions: { strokeColor: '#2563EB', strokeWeight: 6, zIndex: 10 },
        })
        const bounds = new g.LatLngBounds()
        ;[origin, destination, ...waypoints].forEach((p) => bounds.extend(p))
        map.fitBounds(bounds, 64)
        return () => {
            markersRef.current.forEach((m) => m.setMap(null))
            markersRef.current = []
            altLinesRef.current.forEach((l) => l.setMap(null))
            altLinesRef.current = []
            rendererRef.current?.setMap(null)
            rendererRef.current = null
            mapRef.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Redraw draggable markers + recompute the route whenever a point changes (drag / add / remove). No refit,
    // so dragging never jumps the view.
    useEffect(() => {
        const g = window.google?.maps
        const map = mapRef.current
        if (!g || !map) return

        markersRef.current.forEach((m) => m.setMap(null))
        const makeMarker = (position: LatLng, color: string, letter: string, onChange: (ll: LatLng) => void): google.maps.Marker => {
            const marker = new g.Marker({ position, map, draggable: editable, icon: pinIcon(g, color, letter) })
            marker.addListener('dragend', (e: google.maps.MapMouseEvent) => {
                const ll = toLatLng(e.latLng?.lat(), e.latLng?.lng())
                if (ll) onChange(ll)
            })
            return marker
        }
        markersRef.current = [
            makeMarker(origin, ROUTE_PIN_ORIGIN_COLOR, 'A', (ll) => cbRef.current.onOriginChange(ll)),
            makeMarker(destination, ROUTE_PIN_DESTINATION_COLOR, 'B', (ll) => cbRef.current.onDestinationChange(ll)),
            ...waypoints.map((wp, i) => makeMarker(wp, '#2563EB', String(i + 1), (ll) => cbRef.current.onWaypointChange(i, ll))),
        ]

        const renderer = rendererRef.current
        if (renderer) {
            new g.DirectionsService().route(
                {
                    origin,
                    destination,
                    waypoints: waypoints.map((w) => ({ location: w, stopover: true })),
                    travelMode: g.TravelMode.DRIVING,
                    provideRouteAlternatives: true,
                },
                (result, status) => {
                    if (status === g.DirectionsStatus.OK && result) {
                        resultRef.current = result
                        renderer.setDirections(result)
                        // A fresh route always resets to the primary — the previously-selected alternate may no
                        // longer exist after a drag/add/remove; the panel re-syncs its own index via onRoutes.
                        renderer.setRouteIndex(0)
                        drawAlternates(0)
                        cbRef.current.onRoutes?.(result.routes.map(summarizeRoute))
                    } else {
                        console.warn('[box-truck] editable route directions failed', status)
                        resultRef.current = null
                        drawAlternates(0)
                        cbRef.current.onRoutes?.([])
                    }
                }
            )
        }
        // `editable` is in here because it decides how the markers are BUILT: a change has to redraw them,
        // not wait for the next point to move.
    }, [origin, destination, waypoints, editable])

    // Switch the rendered/highlighted route when the panel (or a map-click) selects an alternate — WITHOUT
    // recomputing: the renderer already holds the directions, so we just re-index it and redraw the grey lines.
    useEffect(() => {
        const renderer = rendererRef.current
        // Clamp to the routes actually held — a stale/out-of-range index would make setRouteIndex throw.
        const count = resultRef.current?.routes.length ?? 0
        const idx = count > 0 ? Math.min(Math.max(routeIndex, 0), count - 1) : 0
        if (renderer && renderer.getDirections()) renderer.setRouteIndex(idx)
        drawAlternates(idx)
    }, [routeIndex])

    return <div ref={containerRef} className="h-full w-full" />
}
